import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [photo, setPhoto] = useState(null);

  const login = async (e) => {
    e?.preventDefault();
    setError("");
    try {
      const { data, error: rpcErr } = await supabase.rpc("admin_verify", { token });
      if (rpcErr || !data?.verified) throw new Error("Token inválido.");
      localStorage.setItem("admin_token", token);
      setAuthed(true);
    } catch {
      setError("Token inválido.");
      setAuthed(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc("admin_get_leads", { token });
      if (rpcErr) throw rpcErr;
      setLeads(data?.leads ?? []);
    } catch {
      setError("Sessão expirada.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const openLead = async (l) => {
    setSelected(l);
    setPhoto(null);
    try {
      const { data } = await supabase.rpc("admin_get_lead_photo", { token, lead_id: l.id });
      setPhoto(data);
    } catch {
      setPhoto(null);
    }
  };

  const updateStatus = async (id, status) => {
    await supabase.rpc("admin_update_status", { token, lead_id: id, new_status: status });
    await load();
    setSelected((s) => (s ? { ...s, status } : s));
  };

  useEffect(() => {
    if (token) {
      login();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  if (!authed) {
    return (
      <main className="min-h-screen bg-bone text-ink flex items-center justify-center p-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-rule bg-paper p-10"
        >
          <div className="overline text-ink/60 mb-4">Acesso interno</div>
          <h1 className="font-serif font-light text-4xl leading-tight">Painel</h1>
          <p className="mt-4 text-sm text-ink/60">Introduza o token de acesso administrativo.</p>
          <input
            data-testid="admin-token-input"
            type="password"
            className="input-edit mt-10"
            placeholder="Token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
          {error && <div className="mt-4 text-sm text-red-700">{error}</div>}
          <button type="submit" className="btn-primary mt-10 w-full" data-testid="admin-login-submit">
            Entrar
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-bone text-ink">
      <header className="border-b border-rule bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="font-serif italic text-2xl">Sua Piscina</span>
            <span className="overline text-ink/60">Painel</span>
          </div>
          <div className="flex items-center gap-6">
            <button
              onClick={load}
              className="overline text-ink/70 hover:text-ink"
              data-testid="admin-refresh"
            >
              Actualizar
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("admin_token");
                setAuthed(false);
                setToken("");
              }}
              className="overline text-ink/70 hover:text-ink"
              data-testid="admin-logout"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 grid grid-cols-12 gap-6">
        <section className="col-span-12 md:col-span-7">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-3xl md:text-4xl font-light">
              Pedidos <span className="text-ink/50 text-xl">({leads.length})</span>
            </h2>
            {loading && <span className="overline text-ink/60">A carregar</span>}
          </div>
          <div className="border border-rule bg-paper divide-y divide-rule" data-testid="admin-leads-list">
            {leads.length === 0 && !loading && (
              <div className="p-10 text-center text-ink/60">Sem pedidos por agora.</div>
            )}
            {leads.map((l) => (
              <button
                key={l.id}
                onClick={() => openLead(l)}
                data-testid={`admin-lead-row-${l.id}`}
                className={`w-full text-left p-5 hover:bg-sand transition-colors grid grid-cols-12 gap-3 ${
                  selected?.id === l.id ? "bg-sand" : ""
                }`}
              >
                <div className="col-span-4">
                  <div className="font-serif text-lg">{l.name}</div>
                  <div className="text-xs text-ink/60">{l.phone}</div>
                </div>
                <div className="col-span-3 overline text-ink/70 self-center">{l.zone}</div>
                <div className="col-span-3 text-xs text-ink/60 self-center">
                  {new Date(l.created_at).toLocaleString("pt-PT")}
                </div>
                <div className="col-span-2 text-right self-center">
                  <span className="overline text-pool-deep">{l.status}</span>
                </div>
              </button>
            ))}
          </div>
        </section>

        <aside className="col-span-12 md:col-span-5">
          {selected ? (
            <div className="border border-rule bg-paper p-6 sticky top-6" data-testid="admin-lead-detail">
              <div className="overline text-ink/60">Detalhe</div>
              <h3 className="font-serif text-3xl font-light mt-2">{selected.name}</h3>
              <dl className="mt-8 space-y-4 text-sm">
                <div className="flex justify-between"><dt className="overline text-ink/50">Telefone</dt><dd>{selected.phone}</dd></div>
                <div className="flex justify-between"><dt className="overline text-ink/50">Email</dt><dd>{selected.email || "--"}</dd></div>
                <div className="flex justify-between"><dt className="overline text-ink/50">Zona</dt><dd>{selected.zone}</dd></div>
                <div className="flex justify-between gap-3"><dt className="overline text-ink/50">Prazo</dt><dd className="text-right">{selected.timeline || "--"}</dd></div>
                <div className="flex justify-between gap-3"><dt className="overline text-ink/50">Intenção</dt><dd className="text-right">{selected.intent || "--"}</dd></div>
                <div className="flex justify-between"><dt className="overline text-ink/50">Estado</dt><dd>{selected.status}</dd></div>
              </dl>
              {(selected.utm_source || selected.utm_medium || selected.utm_campaign || selected.referrer) && (
                <div className="mt-6 pt-6 border-t border-rule">
                  <div className="overline text-ink/50 mb-3">Atribuição</div>
                  <dl className="space-y-2 text-xs text-ink/70">
                    {selected.utm_source && <div className="flex justify-between gap-3"><dt className="overline text-ink/50">Source</dt><dd className="truncate">{selected.utm_source}</dd></div>}
                    {selected.utm_medium && <div className="flex justify-between gap-3"><dt className="overline text-ink/50">Medium</dt><dd className="truncate">{selected.utm_medium}</dd></div>}
                    {selected.utm_campaign && <div className="flex justify-between gap-3"><dt className="overline text-ink/50">Campaign</dt><dd className="truncate">{selected.utm_campaign}</dd></div>}
                    {selected.utm_content && <div className="flex justify-between gap-3"><dt className="overline text-ink/50">Content</dt><dd className="truncate">{selected.utm_content}</dd></div>}
                    {selected.utm_term && <div className="flex justify-between gap-3"><dt className="overline text-ink/50">Term</dt><dd className="truncate">{selected.utm_term}</dd></div>}
                    {selected.referrer && <div className="flex justify-between gap-3"><dt className="overline text-ink/50">Referrer</dt><dd className="truncate max-w-[60%]" title={selected.referrer}>{selected.referrer}</dd></div>}
                    {selected.landing_page && <div className="flex justify-between gap-3"><dt className="overline text-ink/50">Landing</dt><dd className="truncate max-w-[60%]" title={selected.landing_page}>{selected.landing_page}</dd></div>}
                  </dl>
                </div>
              )}
              {selected.notes && (
                <div className="mt-6">
                  <div className="overline text-ink/50 mb-2">Nota</div>
                  <p className="text-sm text-ink/80">{selected.notes}</p>
                </div>
              )}
              {photo?.photo_base64 && (
                <div className="mt-6">
                  <div className="overline text-ink/50 mb-2">Fotografia</div>
                  <img src={photo.photo_base64} alt="Jardim" className="w-full border border-rule" />
                </div>
              )}
              <div className="mt-8 pt-6 border-t border-rule grid grid-cols-3 gap-2">
                {["new", "contactado", "fechado"].map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected.id, s)}
                    className={`overline py-3 border ${
                      selected.status === s
                        ? "bg-ink text-bone border-ink"
                        : "border-rule text-ink/70 hover:border-ink"
                    }`}
                    data-testid={`admin-status-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-rule p-10 text-center text-ink/50">
              Seleccione um pedido
            </div>
          )}
        </aside>
      </div>
    </main>
  );
}
